import React, { useState, useEffect, useContext } from "react";
import { User, Tag, Bell, Lock } from "lucide-react";
import DistributorMasterLayout from "../../../layouts/distributor/DistributorMasterLayout";
import "../../../assets/styles/distributor/DistributorSettings.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";

const TABS = [
  { key: "general",       label: "General",       Icon: User },
  { key: "pricing",       label: "Pricing",        Icon: Tag  },
  { key: "notifications", label: "Notifications",  Icon: Bell },
  { key: "security",      label: "Security",       Icon: Lock },
];

const showAlert = (success, message) => {
  Swal.fire({
    icon: success ? "success" : "error",
    title: success ? "Done!" : "Oops!",
    text: message,
    timer: 2000,
    showConfirmButton: false,
  });
};

/* =============================================
   GENERAL TAB
   ============================================= */
const GeneralTab = ({ baseUrl, distributorId }) => {
  const [fullName, setFullName] = useState("");
  const [email,    setEmail]    = useState("");
  const [phone,    setPhone]    = useState("");
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!distributorId) return;

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);

    axios
      .post(`${baseUrl}/distributor/setting/getProfile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("General getProfile response:", res.data);
        if (res.data.success) {
          const d = res.data.distributor;
          setFullName(d.fullName     || "");
          setEmail(d.email           || "");
          setPhone(d.phonenumber     || "");
        }
      })
      .catch((err) => {
        console.log("General getProfile error:", err.message);
        showAlert(false, "Could not load profile");
      });
  }, [distributorId]);

  const handleSave = () => {
    if (!distributorId) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);
    formData.append("fullName", fullName);
    formData.append("email",    email);
    formData.append("phone",    phone);

    console.log("General updateProfile sending:", { distributorId, fullName, email, phone });

    axios
      .post(`${baseUrl}/distributor/setting/updateProfile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("General updateProfile response:", res.data);
        showAlert(res.data.success, res.data.message);
      })
      .catch((err) => {
        console.log("General updateProfile error:", err.message);
        showAlert(false, "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="distSettings-card">
      <div className="distSettings-section-title">Profile Information</div>

      <div className="distSettings-form-grid">
        <div className="distSettings-form-field">
          <label>Full Name</label>
          <input
            className="distSettings-form-input"
            name="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="distSettings-form-field">
          <label>Email</label>
          <input
            className="distSettings-form-input"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="distSettings-form-field">
          <label>Phone</label>
          <input
            className="distSettings-form-input"
            name="phone"
            value={phone}
            disabled
          />
        </div>
      </div>

      <button
        className="distSettings-save-btn"
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
};

/* =============================================
   PRICING TAB
   ============================================= */
const PricingTab = ({ baseUrl, distributorId }) => {
  const [price,   setPrice]   = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!distributorId) return;

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);

    axios
      .post(`${baseUrl}/distributor/setting/getProfile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("Pricing getProfile response:", res.data);
        if (res.data.success) {
          setPrice(res.data.distributor.price_per_liter || "");
        }
      })
      .catch((err) => {
        console.log("Pricing getProfile error:", err.message);
        showAlert(false, "Could not load pricing");
      });
  }, [distributorId]);

  const handleUpdate = () => {
    if (!distributorId) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);
    formData.append("price", price);

    console.log("Pricing updatePricing sending:", { distributorId, price });

    axios
      .post(`${baseUrl}/distributor/setting/updatePricing`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("Pricing updatePricing response:", res.data);
        showAlert(res.data.success, res.data.message);
      })
      .catch((err) => {
        console.log("Pricing updatePricing error:", err.message);
        showAlert(false, "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="distSettings-card">
      <div className="distSettings-section-title">Milk Pricing</div>

      <div className="distSettings-pw-field">
        <label>Default Price Per Liter (PKR)</label>
        <div className="distSettings-price-wrap">
          <input
            className="distSettings-form-input"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        <span className="distSettings-price-hint">
          This is your default price. Area-specific pricing can be set in Area Management.
        </span>
      </div>

      <button
        className="distSettings-save-btn"
        onClick={handleUpdate}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update Pricing"}
      </button>
    </div>
  );
};

/* =============================================
   NOTIFICATIONS TAB
   ============================================= */
const NotificationsTab = ({ baseUrl, distributorId }) => {
  const [prefs,   setPrefs]   = useState({ email: false, inApp: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!distributorId) return;

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);

    axios
      .post(`${baseUrl}/distributor/setting/getProfile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("Notifications getProfile response:", res.data);
        if (res.data.success) {
          const d = res.data.distributor;
          setPrefs({
            email: d.email_notifications == 1,
            inApp: d.app_notifications   == 1,
          });
        }
      })
      .catch((err) => {
        console.log("Notifications getProfile error:", err.message);
        showAlert(false, "Could not load notification preferences");
      });
  }, [distributorId]);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const handleSave = () => {
    if (!distributorId) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);
    formData.append("email_notifications", prefs.email ? "1" : "0");
    formData.append("app_notifications",   prefs.inApp ? "1" : "0");

    console.log("Notifications updateNotifications sending:", { distributorId, prefs });

    axios
      .post(`${baseUrl}/distributor/setting/updateNotifications`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("Notifications updateNotifications response:", res.data);
        showAlert(res.data.success, res.data.message);
      })
      .catch((err) => {
        console.log("Notifications updateNotifications error:", err.message);
        showAlert(false, "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="distSettings-card">
      <div className="distSettings-section-title">Notification Preferences</div>

      <div className="distSettings-toggle-row">
        <div className="distSettings-toggle-label">
          <strong>Email Notifications</strong>
          <span>Receive updates via email</span>
        </div>
        <label className="distSettings-toggle">
          <input
            type="checkbox"
            checked={prefs.email}
            onChange={() => toggle("email")}
          />
          <span className="distSettings-toggle-slider" />
        </label>
      </div>

      <div className="distSettings-toggle-row">
        <div className="distSettings-toggle-label">
          <strong>In-App Notifications</strong>
          <span>Receive in-app push notifications</span>
        </div>
        <label className="distSettings-toggle">
          <input
            type="checkbox"
            checked={prefs.inApp}
            onChange={() => toggle("inApp")}
          />
          <span className="distSettings-toggle-slider" />
        </label>
      </div>

      <button
        className="distSettings-save-btn"
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save Preferences"}
      </button>
    </div>
  );
};

/* =============================================
   SECURITY TAB
   ============================================= */
const SecurityTab = ({ baseUrl, distributorId }) => {
  const [form, setForm] = useState({
    current: "",
    newPw:   "",
    confirm: "",
  });
  const [loading, setLoading] = useState(false);

  const handle = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleUpdate = () => {
    if (!distributorId) return;

    if (!form.current || !form.newPw || !form.confirm) {
      return showAlert(false, "Please fill all password fields");
    }
    if (form.newPw !== form.confirm) {
      return showAlert(false, "New passwords do not match");
    }
    if (form.newPw.length < 6) {
      return showAlert(false, "Password must be at least 6 characters");
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("mwdistributor_id", distributorId);
    formData.append("current", form.current);
    formData.append("newPw",   form.newPw);
    formData.append("confirm", form.confirm);

    console.log("Security changePassword sending:", { distributorId, current: form.current });

    axios
      .post(`${baseUrl}/distributor/setting/changePassword`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("Security changePassword response:", res.data);
        showAlert(res.data.success, res.data.message);
        if (res.data.success) {
          setForm({ current: "", newPw: "", confirm: "" });
        }
      })
      .catch((err) => {
        console.log("Security changePassword error:", err.message);
        showAlert(false, "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="distSettings-card">
      <div className="distSettings-section-title">Change Password</div>

      <div className="distSettings-pw-fields">
        <div className="distSettings-pw-field">
          <label>Current Password</label>
          <input
            className="distSettings-form-input"
            type="password"
            name="current"
            value={form.current}
            onChange={handle}
          />
        </div>
        <div className="distSettings-pw-field">
          <label>New Password</label>
          <input
            className="distSettings-form-input"
            type="password"
            name="newPw"
            value={form.newPw}
            onChange={handle}
          />
        </div>
        <div className="distSettings-pw-field">
          <label>Confirm New Password</label>
          <input
            className="distSettings-form-input"
            type="password"
            name="confirm"
            value={form.confirm}
            onChange={handle}
          />
        </div>
      </div>

      <button
        className="distSettings-save-btn"
        onClick={handleUpdate}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update Password"}
      </button>
    </div>
  );
};

/* =============================================
   MAIN
   ============================================= */
const SettingsContent = () => {
  const [activeTab, setActiveTab] = useState("general");
  const { baseUrl } = useContext(AppContext);
  const distributorId = localStorage.getItem("distributorId");

  console.log("SettingsContent distributorId:", distributorId);

  const tabProps = { baseUrl, distributorId };

  const renderTab = () => {
    switch (activeTab) {
      case "general":       return <GeneralTab       {...tabProps} />;
      case "pricing":       return <PricingTab        {...tabProps} />;
      case "notifications": return <NotificationsTab  {...tabProps} />;
      case "security":      return <SecurityTab        {...tabProps} />;
      default:              return <GeneralTab         {...tabProps} />;
    }
  };

  return (
    <div className="distSettings-page">
      <div className="distSettings-header">
        <h2>Settings</h2>
        <p>Manage your account and preferences</p>
      </div>

      <div className="distSettings-tabs">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`distSettings-tab-btn ${activeTab === key ? "active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
};

const DistributorSettings = () => (
  <DistributorMasterLayout>
    <SettingsContent />
  </DistributorMasterLayout>
);

export default DistributorSettings;